import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import RevealText from "./RevealText";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1517913451214-e22ce660e086?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzZWN1cml0eSUyMGd1YXJkJTIwcGF0cm9sJTIwbmlnaHR8ZW58MHx8fHwxNzg1MTA1NzIwfDA&ixlib=rb-4.1.0&q=85";

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Parallax: image drifts up as user scrolls
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const spotlightOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.6, 0.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  return (
    <section
      ref={ref}
      data-testid="kinetic-hero"
      className="relative h-[100svh] w-full overflow-hidden bg-[#050505]"
    >
      {/* Parallax image layer */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 z-0"
      >
        <img
          src={HERO_IMAGE}
          alt="Security officer silhouette in cinematic light"
          className="h-full w-full object-cover grayscale contrast-125 opacity-70"
          loading="eager"
        />
      </motion.div>

      {/* Spotlight vignette */}
      <motion.div
        style={{ opacity: spotlightOpacity }}
        className="absolute inset-0 z-10 pointer-events-none"
        aria-hidden
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 45% at 65% 35%, transparent 0%, rgba(5,5,5,0.6) 55%, rgba(5,5,5,0.95) 100%)",
          }}
        />
        <div className="absolute inset-0 bg-[#050505]/40" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: textY }}
        className="absolute inset-0 z-20 flex flex-col justify-between px-6 md:px-10 pt-32 md:pt-40 pb-12"
      >
        <div className="max-w-[1600px] mx-auto w-full grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-2">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
              className="font-editorial italic text-[#FF3B30] text-lg"
            >
              File No.
              <br />
              <span className="text-white text-6xl leading-none">01</span>
            </motion.p>
          </div>

          <div className="col-span-12 md:col-span-10 space-y-8">
            <RevealText
              lines={["MANITOBA'S", "STANDING", "SECURITY", "AUTHORITY."]}
              className="font-display font-black uppercase text-white tracking-tightest leading-[1.05] text-[28px]"
              stagger={0.12}
              delay={0.2}
              duration={1.15}
            />

            {/* Filler content — editorial subhead + stats + key service chips */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.9 }}
              className="font-editorial italic text-[#a0a0a0] text-2xl md:text-4xl leading-tight max-w-3xl"
            >
              Fifteen disciplines. Forty communities. One operating standard —
              executed from downtown Winnipeg to the fly-in camps of the
              northern nickel belt.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.9 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10 pt-6 border-t border-[#1e1e1e] max-w-4xl"
            >
              {[
                { n: "15", l: "Disciplines" },
                { n: "40+", l: "Communities" },
                { n: "24/7", l: "Operational" },
                { n: "1", l: "Standard" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-display font-black text-white text-4xl md:text-5xl leading-none">
                    {s.n}
                  </p>
                  <p className="mt-2 font-body uppercase tracking-[0.14em] text-[#a0a0a0] text-[10px] md:text-xs">
                    {s.l}
                  </p>
                </div>
              ))}
            </motion.div>

            <motion.ul
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.35, duration: 0.9 }}
              className="flex flex-wrap gap-x-3 gap-y-2 pt-2 max-w-4xl"
            >
              {[
                "Mining",
                "Mobile Patrol",
                "Fire Watch",
                "Event",
                "Corporate",
                "Construction",
                "Executive Protection",
                "Healthcare",
              ].map((chip) => (
                <li
                  key={chip}
                  className="font-body text-[10px] md:text-xs uppercase tracking-[0.14em] text-white/85 border border-[#2a2a2a] px-3 py-1 hover:border-[#FF3B30] transition-colors"
                >
                  {chip}
                </li>
              ))}
            </motion.ul>
          </div>
        </div>

        <div className="max-w-[1600px] mx-auto w-full grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-5">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.9 }}
              className="font-body text-sm md:text-base text-white/85 leading-relaxed max-w-md"
            >
              Guard services calibrated to a province the size of a country.
              Mining sites, corporate lobbies, event floors, and the highway
              grid that connects them — one operating standard, executed
              province-wide.
            </motion.p>
          </div>

          <div className="hidden md:block col-span-4" />

          <div className="col-span-12 md:col-span-3 flex md:justify-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.6, duration: 0.9 }}
              className="text-right"
            >
              <p className="font-editorial italic text-[#a0a0a0] text-sm">
                Coordinates
              </p>
              <p className="font-display text-white text-lg">
                49.895° N / 97.138° W
              </p>
              <p className="font-body text-xs uppercase tracking-[0.18em] text-[#a0a0a0] mt-1">
                Winnipeg · Manitoba
              </p>
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 z-30 hidden md:flex flex-col items-center gap-2 pointer-events-none"
      >
        <span className="font-editorial italic text-xs text-[#a0a0a0]">
          Scroll
        </span>
        <div className="relative h-10 w-[1px] bg-[#2a2a2a] overflow-hidden">
          <motion.div
            className="absolute inset-x-0 top-0 h-4 bg-[#FF3B30]"
            animate={{ y: [-16, 40] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
      </motion.div>
    </section>
  );
}
